import React from "react";
import { Redirect, Route, BrowserRouter as Router, Switch, useLocation } from "react-router-dom";

import "react-responsive-carousel/lib/styles/carousel.min.css";
import "./assets/css/bootstrap.min.css";
import "./index.css";

import NewAboutPage from "./containers/AboutPage/NewAboutPage";
import AccountPage from "./containers/AccountPage/AccountPage";
import DashboardPage from "./containers/DashboardPage/DashboardPage";
import NewFAQPage from "./containers/FAQPage/NewFAQPage";
import GetStartedPage from "./containers/GetStartedPage/GetStartedPage";
import NewLandingPage from "./containers/LandingPage/NewLandingPage";
import NewListingConfirmation from "./containers/ListingConfirmation/NewListingConfirmation";
import LoginPage from "./containers/LoginPage/LoginPage";
import ManualSubmitPage from "./containers/ManualSubmitPage/ManualSubmitPage";
import NotFoundPage from "./containers/NotFoundPage/NotFoundPage";
import PaymentSuccess from "./containers/PaymentSuccess";
import PhotoVideoPage from "./containers/PhotoVideoPage/PhotoVideoPage";
import NewPricingPage from "./containers/PricingPage/NewPricingPage";
import PrivacyPolicyPage from "./containers/PrivacyPolicyPage";
import PropertyPage from "./containers/PropertyPage/PropertyPage";
import ReportEditorPage from "./containers/ReportEditorPage/ReportEditorPage";
import ResourcePage from "./containers/ResourcesPage/ResourcePage";
import ResourcesIndexPage from "./containers/ResourcesPage/ResourcesIndexPage";
import SharePage from "./containers/SharePage/SharePage";
import SharedReportPage from "./containers/SharedReportPage/SharedReportPage";
import SimplifiedSubmitPage from "./containers/SubmitListingPage/SimplifiedSubmitPage";
import TermsAndConditionsPage from "./containers/TermsAndConditionsPage";
import ToolPage from "./containers/ToolsPage/ToolPage";
import TouchupsPage from "./containers/TouchupsPage/TouchupsPage";

import Seo from "./components/Seo/Seo";
import { API_URL } from "./constants";
import { CREDITS_PACK_KEY, clearCreditsReturn, readCreditsReturn } from "./constants/credits";
import { getSeo } from "./constants/seo";
import { AccountProvider } from "./contexts/AccountContext";
import { storageRemove, storageSet } from "./utils/storage";

// Capture a coupon code arriving via URL (?coupon=CODE) — QR codes and
// outreach emails link straight to the site with the code attached. Stash
// it for the plan checkouts (the paywall sheet and /pricing), which send it
// with POST /subscribe/.
const CouponCapture = () => {
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const coupon = (params.get("coupon") || "").trim().toUpperCase();
    if (coupon) {
      storageSet("pendingCouponCode", coupon);
    }
  }, []);
  return null;
};

// Stripe sends a cancelled checkout here. The sheet that started it stashed
// where it opened (an editor, the share page, the Prepare sheet, the chip),
// so return there with the listing untouched.
const CancelReturn = () => {
  const [target] = React.useState(() => {
    storageRemove(CREDITS_PACK_KEY);
    const stash = readCreditsReturn();
    clearCreditsReturn();
    return stash?.path || "/listing-confirmation/";
  });
  return <Redirect to={target} />;
};

const ScrollToTop = () => {
  const location = useLocation();
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run on every route change
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return null;
};

// Centralized per-route SEO for the static marketing + funnel pages. Resource
// and tool pages render their own <Seo>, so getSeo returns null for them.
const RouteSeo = () => {
  const location = useLocation();
  const seo = getSeo(location.pathname);
  return seo ? <Seo {...seo} /> : null;
};

// Debug banner component for development mode
const DebugBanner = () => {
  const [envInfo, setEnvInfo] = React.useState(
    /** @type {{ is_production_db?: boolean; database_name?: string } | null} */ (null)
  );

  React.useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    fetch(`${API_URL}/dev/env-info/`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => setEnvInfo(data))
      .catch(() => setEnvInfo(null));
  }, []);

  if (process.env.NODE_ENV !== "development") return null;

  const isProductionDb = envInfo?.is_production_db;
  const dbName = envInfo?.database_name;

  /** @type {React.CSSProperties} */
  const bannerStyle = {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    padding: "8px 16px",
    textAlign: "center",
    fontSize: "12px",
    fontWeight: "bold",
    zIndex: 9999,
    boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', monospace",
    letterSpacing: "0.5px",
  };

  if (isProductionDb) {
    return (
      <div
        style={{
          ...bannerStyle,
          backgroundColor: "#dc3545",
          color: "white",
          animation: "pulse-bg 1.5s ease-in-out infinite",
        }}
      >
        <style>
          {
            "@keyframes pulse-bg { 0%, 100% { background-color: #dc3545; } 50% { background-color: #a71d2a; } }"
          }
        </style>
        DEV MODE | API: {API_URL} | PRODUCTION DB: {dbName}
      </div>
    );
  }

  return (
    <div
      style={{
        ...bannerStyle,
        backgroundColor: "#28a745",
        color: "white",
      }}
    >
      DEV MODE | API: {API_URL} | {dbName ? `LOCAL DB: ${dbName}` : "DB: loading..."}
    </div>
  );
};

function App() {
  return (
    <div>
      <Router>
        <CouponCapture />
        <ScrollToTop />
        <RouteSeo />
        <AccountProvider>
          <Switch>
            <Route exact path="/">
              <NewLandingPage />
            </Route>

            <Route path="/get-started">
              <GetStartedPage />
            </Route>

            <Route path="/submit">
              <ManualSubmitPage />
            </Route>

            <Route path={"/verify"}>
              <SimplifiedSubmitPage />
            </Route>

            <Route path="/pricing">
              <NewPricingPage />
            </Route>

            <Route path="/listing-photos-and-video">
              <PhotoVideoPage />
            </Route>

            <Route path="/faq">
              <NewFAQPage />
            </Route>

            <Route exact path="/resources">
              <ResourcesIndexPage />
            </Route>
            <Route path="/resources/:slug">
              <ResourcePage />
            </Route>

            <Route path="/tools/:slug">
              <ToolPage />
            </Route>

            <Route path="/listing-confirmation/">
              <NewListingConfirmation />
            </Route>

            <Route path="/success">
              <PaymentSuccess />
            </Route>
            <Route path="/subscription-success">
              <PaymentSuccess subscription />
            </Route>

            {/* /cancel - Stripe redirects here when user cancels checkout */}
            <Route path="/cancel">
              <CancelReturn />
            </Route>

            <Route path="/about">
              <NewAboutPage />
            </Route>

            <Route path="/privacy-policy">
              <PrivacyPolicyPage />
            </Route>

            <Route path="/terms-and-conditions">
              <TermsAndConditionsPage />
            </Route>

            <Route path="/touchups/:uuid">
              <TouchupsPage />
            </Route>

            <Route path="/shared/:token">
              <SharedReportPage />
            </Route>

            <Route path="/reports/:uuid/edit">
              <ReportEditorPage />
            </Route>

            <Route path="/reports/:uuid/share">
              <SharePage />
            </Route>

            <Route path="/login">
              <LoginPage />
            </Route>

            <Route path="/dashboard">
              <DashboardPage />
            </Route>

            <Route path="/account">
              <AccountPage />
            </Route>

            <Route path="/properties/:listingUuid">
              <PropertyPage />
            </Route>

            {/* Redirect old routes */}
            {/* Social content used to be a page of its own; captions and
              snapshots now sit on the package page, so its links land there. */}
            <Redirect from="/reports/:uuid/content" to="/reports/:uuid/share" />
            <Route path="/contact">
              <Redirect to="/faq" />
            </Route>

            {/* <Route
  path="/dev/listing-confirmation"
  component={NewListingConfirmationPreview}
/>

<Route exact path="/dev/dashboard">
  <DashboardPagePreview />
</Route> */}

            {/* 404 - real not-found page (noindex), not a soft redirect to home.
              Note: CloudFront still serves index.html with a 200 for unknown
              paths (SPA error-response config). For a true 404 status, see the
              prerendering design doc — the edge function should return 404 for
              bot requests to unknown URLs. */}
            <Route>
              <NotFoundPage />
            </Route>
          </Switch>
        </AccountProvider>
      </Router>
      <DebugBanner />
    </div>
  );
}

export default App;
